import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '../../helpers.js';

// ─────────────────────────────────────────────────────────────────
// Switch — the product's single toggle control.
//
// There is no native switch element, so the visible track and knob are
// painted as *siblings* of a real `<input type="checkbox" role="switch">`
// that is hidden with `sr-only`. Keeping the native input is what buys
// the behaviour for free:
//   · Space toggles it, and the whole `<label>` is a click target;
//   · forms still see it through `name`/`value`/`required`;
//   · `role="switch"` makes a screen reader announce "switch" rather
//     than "checkbox";
//   · every painted state comes from `peer-checked:` /
//     `peer-focus-visible:` — no JavaScript reads the state at all, which
//     is why controlled (`checked`) and uncontrolled (`defaultChecked`)
//     work identically here. `disabled` needs no painted variant either:
//     the native attribute blocks the click and a dimmed row reads it.
// The knob has to be a sibling of the input for those `peer-*` variants
// to resolve (`~` is a sibling combinator), so both live inside a plain
// positioning wrapper rather than the knob sitting inside the track.
//
// Design follows the tokens, not the usual "iOS green": off is a
// card-2 recess separated by a hairline, on is the one accent fill. The
// knob is deliberately *not* Paper White in the on state — #3fc1c9
// cannot carry white (see globals.css), so it takes `accent-ink`, the
// documented ink that sits on the accent fill, which flips its twin in
// dark mode alongside the accent. Focus reuses the global `--focus-ring`
// so a switch is ringed exactly like every other control.
// ─────────────────────────────────────────────────────────────────

export type SwitchSize = 'sm' | 'md';

/**
 * Per-size geometry, written as whole class strings because Tailwind only
 * sees classes that appear literally in the source.
 *
 * The knob reproduces the track's *content* box, which `box-sizing:
 * border-box` sizes at `track − 2×border − 2×p-0.5` — i.e. 3px of inset
 * (1px border + 2px padding) on every side, matching the `top-[3px]
 * left-[3px]` the knob is placed with below:
 *   sm — 28−2−4 = 22 wide, 16−2−4 = 10 tall → 10px knob, 12px of travel
 *   md — 36−2−4 = 30 wide, 20−2−4 = 14 tall → 14px knob, 16px of travel
 * Travel is simply what is left over: `width − 2×3px inset − knob width`,
 * so the knob lands equidistant from both ends. Nothing is assumed about
 * the padding box doing layout work — it only defines that inset.
 */
const SIZES = {
  sm: {
    track: 'h-4 w-7',
    knob: 'h-2.5 w-2.5',
    travel: 'peer-checked:translate-x-3',
  },
  md: {
    track: 'h-5 w-9',
    knob: 'h-3.5 w-3.5',
    travel: 'peer-checked:translate-x-4',
  },
} as const satisfies Record<SwitchSize, { track: string; knob: string; travel: string }>;

export interface SwitchProps extends Omit<
  ComponentPropsWithoutRef<'input'>,
  'type' | 'size' | 'checked' | 'defaultChecked' | 'onChange' | 'className' | 'children'
> {
  /** Controlled state. Pair with `onChange`; omit for an uncontrolled switch. */
  checked?: boolean;
  /** Initial state of an uncontrolled switch. */
  defaultChecked?: boolean;
  /** Receives the *next* state, not the DOM event — `checked` is the whole story. */
  onChange?: (checked: boolean) => void;
  /** Text beside the switch; also the input's accessible name. */
  label?: ReactNode;
  /** Second, quieter line under `label`. Plain text only — a link inside it
   *  would sit in the click target and toggle the switch. */
  description?: ReactNode;
  /** `sm` (16px tall) for dense rows, `md` (20px, default) elsewhere. */
  size?: SwitchSize;
  disabled?: boolean;
  /** `end` (default) puts the text after the switch; `start` puts it first and
   *  stretches the row, for a settings row like "Enable dark mode    [ o]". */
  labelPosition?: 'start' | 'end';
  className?: string;
}

/**
 * A labelled switch. Renders the input *and* its label, so pass
 * `aria-label` when there is no visible `label` to steal a name from.
 *
 * ```tsx
 * const [weekly, setWeekly] = useState(true);
 * <Switch
 *   checked={weekly}
 *   onChange={setWeekly}
 *   label="Weekly digest"
 *   description="Every Monday at 09:00"
 * />
 * ```
 */
export function Switch({
  checked,
  defaultChecked,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  labelPosition = 'end',
  className,
  ...rest
}: SwitchProps) {
  const geometry = SIZES[size];
  const isReversed = labelPosition === 'start';

  const caption =
    label || description ? (
      <span className="flex min-w-0 flex-col">
        {label ? <span className="text-body-sm text-ink font-medium">{label}</span> : null}
        {description ? <span className="text-caption text-ink-soft">{description}</span> : null}
      </span>
    ) : null;

  return (
    <label
      className={cn(
        // `group` exists only so hovering *anywhere* on the row — the
        // caption included — previews the state the click would produce.
        'group inline-flex gap-2.5 select-none',
        // `items-start` keeps a two-line caption aligned with the switch
        // instead of drifting it to the middle of the text block.
        isReversed ? 'w-full flex-row items-center justify-between' : 'items-start',
        disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
        className,
      )}
    >
      {isReversed ? caption : null}

      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          role="switch"
          className="peer sr-only"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.checked)}
          {...rest}
        />

        {/* Track — the recess. Every visual state is a `peer-*` variant,
            and the two hover rules are split on `peer-checked` so an
            unchecked and a checked track can never fight over the
            background (both would otherwise match a hovered, checked
            switch). `group-hover:` is inside `@media (hover: hover)`, so
            a touch device never sticks in a hover state. */}
        <span
          aria-hidden="true"
          className={cn(
            'bg-card-2 border-line rounded-pill inline-flex items-center border p-0.5 transition-colors',
            'not-peer-checked:group-hover:border-line-strong',
            'peer-checked:bg-accent peer-checked:border-transparent',
            'peer-checked:group-hover:bg-accent-deep',
            'peer-focus-visible:[outline:2px_solid_var(--focus-ring)] peer-focus-visible:[outline-offset:3px]',
            geometry.track,
          )}
        />

        <span
          aria-hidden="true"
          className={cn(
            'bg-card border-line-strong rounded-pill absolute top-[3px] left-[3px] border',
            'transition-[translate,background-color,border-color]',
            'peer-checked:bg-accent-ink peer-checked:border-transparent',
            geometry.knob,
            geometry.travel,
          )}
        />
      </span>

      {isReversed ? null : caption}
    </label>
  );
}
